import {
  drugInfluenceRows,
  HTN_DRUG_META,
} from '../../utils/htnBlister';

// Data helpers that resolve WHICH equipped HTN drugs contribute to a given combat
// status axis (potassium / pulse / diuresis).
//
// Edward 2026-07-21: the combat status tags are DRUG-AGNOSTIC. A status — serum
// potassium, a Diuresis stack, a pulse shift — is an aggregate combat/body state,
// not one drug's property (multiple drugs can feed a Diuresis stack; serum K is the
// NET of the whole regimen). So the status tag tooltip describes the STATUS itself;
// the drug is only its initiator and rides a light "Fed by / Shifted by · <drug(s)>"
// provenance footnote built from these helpers. The per-drug influence ROWS live on
// the DRUG surfaces (the equipped-effect chip + the drug toast), never on the status
// tag. (This module formerly rendered a per-drug card tooltip for the status tags —
// removed as a category error: a status isn't a drug.)

const ROW_MATCHERS = {
  potassium: (row) => row.type === 'influences-potassium',
  pulse: (row) => row.type === 'influences-pulse',
  diuresis: (row) => row.type === 'inflicts' && row.status === 'diuresis',
};

function normalizeSlug(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

// One entry per equipped drug that actually pushes this status axis (has a matching
// canonical row). De-dups by slug so the same drug never lists twice.
export function htnDrugInfluenceGroups(activeEquippedEffects, axis) {
  const matcher = ROW_MATCHERS[axis];
  if (!matcher || !Array.isArray(activeEquippedEffects)) return [];

  const groups = new Map();
  activeEquippedEffects.forEach((effect) => {
    const slug = normalizeSlug(effect?.drug_slug);
    const meta = HTN_DRUG_META[slug];
    if (!slug || !meta || groups.has(slug)) return;
    const row = drugInfluenceRows(slug).find(matcher);
    if (!row) return;
    groups.set(slug, {
      slug,
      abilityName: String(effect?.name || meta.ability || '').trim(),
      drugName: meta.name,
      accent: meta.accent,
      // Canonical per-drug axis facts (mirror of htn_regimen_axes.py) so a consumer
      // can render DIRECTION without re-deriving: k_shift = ±1 (K-retaining raises,
      // kaliuretic lowers), pulse_shift = ±1 (DHP CCBs speed, non-DHP CCBs +
      // beta-blockers slow), nephron_segment = loop/distal/collecting (diuretic
      // sequential-blockade family), class = ace/arb/thiazide/loop/k_sparing_mra/
      // dhp_ccb/nondhp_ccb/beta_blocker.
      kShift: Number(meta.k_shift || 0),
      pulseShift: Number(meta.pulse_shift || 0),
      nephronSegment: meta.nephron_segment || '',
      drugClass: meta.class || '',
      row,
    });
  });
  return Array.from(groups.values());
}

// The generic drug names contributing to a status axis, in equip order, for the
// status tag's light provenance footnote ("Fed by · Chlorthalidone").
export function fedByDrugNames(groups = []) {
  return groups.map((group) => group.drugName).filter(Boolean);
}

// Short pharmacology-class tag shown beside each drug name in the potassium ledger.
const K_SHIFT_CLASS_LABELS = {
  ace: 'ACE',
  arb: 'ARB',
  thiazide: 'thiazide',
  loop: 'loop',
  k_sparing_mra: 'K-sparing',
  carbonic_anhydrase: 'CAI',
  osmotic: 'osmotic',
};

// The per-drug DIRECTION rows for the potassium status tag's "Shifted by" ledger
// (Edward 2026-07-22): each contributing drug on its OWN row as a magnitude-free
// direction — kaliuretic drugs (loop/thiazide) LOWER serum K ('down'), K-retaining
// drugs (ACE/ARB/MRA) RAISE it ('up'). Deliberately no ±mEq: real practice asserts
// direction, not a per-drug quantity. Derived from the same canonical k_shift the
// net band model uses, so the arrows can never diverge from the stated band.
export function potassiumShiftRows(groups = []) {
  return groups
    .filter((group) => Number(group.kShift || 0) !== 0)
    .map((group) => ({
      slug: group.slug,
      drugName: group.drugName,
      classLabel: K_SHIFT_CLASS_LABELS[group.drugClass] || group.drugClass || '',
      direction: Number(group.kShift) > 0 ? 'up' : 'down',
    }));
}

// The aggregate direction word for the ledger's "net" summary. Keyed off the model
// net (which sets the band) — not a re-sum of the rows — so the word always agrees
// with the band the tooltip states. Never a number (Edward: magnitude isn't
// clinically representative).
export function kNetDirectionWord(net) {
  const n = Number(net || 0);
  if (n > 0) return 'higher';
  if (n < 0) return 'lower';
  return 'even';
}

// Diuresis ledger class tags: the nephron segment each diuretic blocks. Covering a
// NEW segment adds that drug's full potency plus a flat cross-segment bonus
// (sequential blockade); a duplicate segment adds nothing at all, because the
// transporter is already saturated. Indapamide/metolazone read as "thiazide-like"
// (distal segment, thiazide class) matching how the design + clinic name them.
const DIURESIS_CLASS_LABELS = {
  loop: 'loop',
  thiazide: 'thiazide',
  k_sparing_mra: 'K-sparing',
  carbonic_anhydrase: 'CAI',
  osmotic: 'osmotic',
};
const THIAZIDE_LIKE_SLUGS = new Set(['indapamide', 'metolazone']);

// The per-drug rows for the Diuresis status tag's "Fed by" ledger (Edward 2026-07-24):
// every equipped diuretic on its OWN row with its nephron-segment class. Direction is
// always 'up' because a diuretic either adds to the drain or leaves it unchanged, and
// never subtracts from it, unlike potassium's bidirectional shift.
export function diuresisFedRows(groups = []) {
  return groups.map((group) => {
    const thiazideLike = group.drugClass === 'thiazide' && THIAZIDE_LIKE_SLUGS.has(group.slug);
    const classLabel = thiazideLike
      ? 'thiazide-like'
      : (DIURESIS_CLASS_LABELS[group.drugClass] || group.drugClass || '');
    return {
      slug: group.slug,
      drugName: group.drugName,
      classLabel,
      direction: 'up',
    };
  });
}

// Pulse ledger class tags — the rate-drug family. DHP CCBs speed the pulse (reflex
// tachycardia), non-DHP CCBs and beta-blockers slow it.
const PULSE_CLASS_LABELS = {
  dhp_ccb: 'DHP CCB',
  nondhp_ccb: 'non-DHP CCB',
  beta_blocker: 'beta-blocker',
};

/* 🔴 THE GAME'S OWN DISPLAY NAME FOR A DRUG CLASS, in one export (2026-08-11).
   The three maps above are the only place this app writes a human name for an
   `axisTagsFor().class` token, and between them they cover all ten classes with
   no gap and no overlap of meaning. The compendium splits HTN REGIMEN into one
   folder per class and needs exactly this; deriving the name instead produced
   NONDHP CCB, K SPARING MRA, BETA BLOCKER and CARBONIC ANHYDRASE, four names the
   game never writes, two of them contradicted by the very cards inside the
   folder.
   ⚠️ COMPOSED FROM THE THREE, NEVER RETYPED. Spreading them means a retune to any
   ledger tag moves this in the same edit; a fourth copy of ten strings would
   drift silently, which is the failure the compendium exists to prevent. */
export const HTN_DRUG_CLASS_LABELS = {
  ...K_SHIFT_CLASS_LABELS,
  ...DIURESIS_CLASS_LABELS,
  ...PULSE_CLASS_LABELS,
};

// The per-drug DIRECTION rows for the pulse status tag's "Shifted by" ledger (Edward
// 2026-07-24): each rate drug on its OWN row as a magnitude-free direction — DHP CCBs
// RAISE the pulse ('up', faster), non-DHP CCBs + beta-blockers LOWER it ('down',
// slower). Derived from the canonical pulse_shift sign, so the arrows can never diverge
// from the stated net.
export function pulseShiftRows(groups = []) {
  return groups
    .filter((group) => Number(group.pulseShift || 0) !== 0)
    .map((group) => ({
      slug: group.slug,
      drugName: group.drugName,
      classLabel: PULSE_CLASS_LABELS[group.drugClass] || group.drugClass || '',
      direction: Number(group.pulseShift) > 0 ? 'up' : 'down',
    }));
}

// The aggregate pulse direction word for the ledger's "net" summary. Keyed off the
// model net (which sets tachy/brady) — not a re-sum of the rows — so the word always
// agrees with the state the tooltip states. Never a number.
export function pulseNetDirectionWord(net) {
  const n = Number(net || 0);
  if (n > 0) return 'faster';
  if (n < 0) return 'slower';
  return 'steady';
}
